import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';

const MAX_LEVEL = 5;

const sizeMap = {
  sm: 'h-5 w-5',
  md: 'h-7 w-7',
  lg: 'h-9 w-9',
};

function ProspectLevelStars({
  value = 0,
  onChange,
  size = 'md',
  readOnly = false,
  centered = false,
  ariaLabel,
  className = '',
}) {
  const [hoverLevel, setHoverLevel] = useState(0);
  const [burstStar, setBurstStar] = useState(null);

  const level = Math.max(0, Math.min(MAX_LEVEL, Number(value) || 0));
  const displayLevel = hoverLevel > 0 ? hoverLevel : level;
  const iconSize = sizeMap[size] || sizeMap.md;
  const interactive = !readOnly && Boolean(onChange);

  function handleSelect(star) {
    if (!interactive) return;
    const next = star === level ? 0 : star;
    setBurstStar(star);
    onChange(next);
    window.setTimeout(() => setBurstStar(null), 480);
  }

  return (
    <div
      className={`inline-flex items-center gap-1 ${centered ? 'w-full justify-center' : ''} ${className}`}
      role={readOnly ? 'img' : 'radiogroup'}
      aria-label={ariaLabel}
      onMouseLeave={() => setHoverLevel(0)}
    >
      {Array.from({ length: MAX_LEVEL }).map((_, index) => {
        const star = index + 1;
        const filled = star <= displayLevel;
        const isBurst = burstStar === star;
        const Star = filled ? StarIcon : StarOutlineIcon;

        const starButton = (
          <>
            <Star
              className={
                filled
                  ? `${iconSize} text-amber-400/90 `
                  : `${iconSize} stroke-[1.5] text-secondary-300  ${
                      interactive && hoverLevel >= star
                        ? 'text-amber-300/70 '
                        : ''
                    }`
              }
            />
            {isBurst ? (
              <motion.span
                className="pointer-events-none absolute inset-0 rounded-full bg-amber-300/25 blur-sm "
                initial={{ scale: 0.6, opacity: 0.7 }}
                animate={{ scale: 1.8, opacity: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            ) : null}
          </>
        );

        if (!interactive) {
          return (
            <span key={star} className="relative inline-flex p-0.5">
              {starButton}
            </span>
          );
        }

        return (
          <motion.button
            key={star}
            type="button"
            className="relative inline-flex rounded-md p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40"
            onMouseEnter={() => setHoverLevel(star)}
            onClick={() => handleSelect(star)}
            aria-label={`${star} ${star === 1 ? 'star' : 'stars'}`}
            aria-checked={star <= level}
            role="radio"
            whileHover={{ scale: 1.12 }}
            whileTap={{ scale: 0.9 }}
            animate={
              isBurst
                ? { scale: [1, 1.3, 1], rotate: [0, -6, 6, 0] }
                : { scale: 1, rotate: 0 }
            }
            transition={{ type: 'spring', stiffness: 480, damping: 14 }}
          >
            {starButton}
          </motion.button>
        );
      })}
    </div>
  );
}

export default ProspectLevelStars;
